/**
 * Scroll-reveal animations shared by the content components (link cards, vertical tabs,
 * accordions — everything except the home hero, which has its own on-load sequence).
 *
 * Markup opts in with three attributes:
 *   data-reveal          a block that reveals as one unit when it scrolls into view
 *   data-reveal-heading    inside it, a heading whose words fade in one by one
 *   data-reveal-item       inside it, anything that slides up
 *   data-reveal-cards    a grid whose children reveal a row at a time, left to right
 *
 * As with the hero, nothing is hidden in the markup or CSS — the starting state is applied here,
 * so a page whose scripts fail still shows all of its content.
 */

import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(ScrollTrigger, SplitText);

const EASE = "power2.out";
const SHIFT = 32;

// Begin once the block is a little way into the viewport rather than right at the edge.
const START = "top 85%";

function hide(elements) {
    if (elements.length > 0) {
        gsap.set(elements, { opacity: 0, y: SHIFT });
    }
}

function revealGroup(group) {
    // Query heading and items together so the sequence follows document order, which is the
    // order they are read in.
    const nodes = Array.from(group.querySelectorAll("[data-reveal-heading], [data-reveal-item]"));

    if (nodes.length === 0) {
        return;
    }

    const splits = [];

    const timeline = gsap.timeline({
        defaults: { ease: EASE },
        scrollTrigger: { trigger: group, start: START, once: true },
        onComplete: () => {
            // Put split headings back to plain text once they have finished animating, so
            // selection, in-page find and screen readers aren't left picking through spans.
            splits.forEach((split) => split.revert());
            gsap.set(nodes, { clearProps: "opacity,transform" });
        },
    });

    nodes.forEach((node, index) => {
        // Overlap each step with the previous one so the block reads as a single reveal.
        const position = index === 0 ? 0 : "<0.25";

        if (node.hasAttribute("data-reveal-heading")) {
            const split = new SplitText(node, { type: "words" });
            splits.push(split);

            // The words carry the reveal from here, so the heading itself can come back.
            gsap.set(node, { opacity: 1, y: 0 });
            gsap.set(split.words, { opacity: 0 });

            timeline.to(split.words, { opacity: 1, duration: 0.6, stagger: 0.04 }, position);
            return;
        }

        timeline.to(node, { opacity: 1, y: 0, duration: 0.6 }, position);
    });
}

/**
 * Groups the grid's children into visual rows by their laid-out position. Reading offsetTop
 * rather than assuming a column count keeps this correct at every breakpoint, and it is
 * unaffected by the transforms the reveal itself applies.
 */
function toRows(cards) {
    const rows = [];
    let rowTop = null;

    cards.forEach((card) => {
        const top = Math.round(card.offsetTop);

        if (rowTop === null || Math.abs(top - rowTop) > 2) {
            rows.push([]);
            rowTop = top;
        }

        rows[rows.length - 1].push(card);
    });

    return rows;
}

function revealCards(grid) {
    const cards = Array.from(grid.children);

    if (cards.length === 0) {
        return;
    }

    // Cards sit in document order, which runs left to right along each row — so a positive
    // stagger is already the left-to-right sweep.
    toRows(cards).forEach((row) => {
        gsap.to(row, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: EASE,
            stagger: 0.12,
            scrollTrigger: { trigger: row[0], start: START, once: true },
        });
    });
}

const groups = Array.from(document.querySelectorAll("[data-reveal]"));
const grids = Array.from(document.querySelectorAll("[data-reveal-cards]"));

if (
    (groups.length > 0 || grids.length > 0) &&
    !window.matchMedia("(prefers-reduced-motion: reduce)").matches
) {
    // Hide up front, synchronously, so nothing is painted in full and then pulled back to the
    // start of its animation while the font below is still resolving.
    groups.forEach((group) => {
        hide(Array.from(group.querySelectorAll("[data-reveal-heading], [data-reveal-item]")));
    });
    grids.forEach((grid) => hide(Array.from(grid.children)));

    // Splitting before the webfont lands would measure the fallback face and leave the words in
    // the wrong places, so hold until the font is resolved.
    document.fonts.ready.then(() => {
        groups.forEach(revealGroup);

        // Row grouping depends on the column count, so rebuild it per breakpoint. matchMedia
        // reverts the animations and scroll triggers made inside a context when it stops
        // matching, so each breakpoint measures the layout fresh.
        const breakpoints = gsap.matchMedia();

        ["(max-width: 639px)", "(min-width: 640px) and (max-width: 1023px)", "(min-width: 1024px)"]
            .forEach((query) => {
                breakpoints.add(query, () => grids.forEach(revealCards));
            });

        ScrollTrigger.refresh();
    });
}
